import React, { useState, useEffect, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { gridTheme, defaultColDef, centeredCellStyle } from '../services/aggrid';
import { Alert, AlertTitle, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useNavigate, useOutletContext } from 'react-router-dom';
import NoAccess from '../components/NoAccess';
import { Panel, Chip, Btn, Dot, PageHead, I } from '../components/hud';

axios.defaults.withCredentials = true;

const API_BASE_URL = (import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:5000')) + '/api';

// Strontium Clathrates type ID — used to fuel POS reinforce timers, not the regular fuel cycle
const STRONTIUM_TYPE_ID = 16275;

// Approximate POS fuel block consumption per hour by tower size (without sov bonus)
const POS_FUEL_RATE = {
  small: 10,
  medium: 20,
  large: 40
};

// Infer tower size from the type name (e.g. "Caldari Control Tower Small")
const getTowerSize = (typeName) => {
  if (!typeName) return 'large';
  const name = typeName.toLowerCase();
  if (name.includes('small')) return 'small';
  if (name.includes('medium')) return 'medium';
  return 'large';
};

// Get type icon URL from EVE image CDN
const getTypeIcon = (typeId) => `https://images.evetech.net/types/${typeId}/icon?size=128`;

// Calculate fuel status for an Upwell structure (uses fuel_expires)
const getStructureFuelStatus = (fuelExpires) => {
  if (!fuelExpires) return { status: 'unknown', days: null };
  const now = new Date();
  const expiryDate = new Date(fuelExpires);
  const hoursRemaining = (expiryDate - now) / (1000 * 60 * 60);
  if (hoursRemaining < 0) return { status: 'expired', days: 0 };
  return { status: 'ok', days: hoursRemaining / 24 };
};

// Find the fuel block (non-strontium) entry for a POS
const getFuelBlock = (starbase) =>
  (starbase.fuels || []).find((f) => f.typeId !== STRONTIUM_TYPE_ID) || null;

const getStrontium = (starbase) =>
  (starbase.fuels || []).find((f) => f.typeId === STRONTIUM_TYPE_ID) || null;

// Estimated fuel hours remaining for a POS
const estimatePosHours = (starbase) => {
  const fuelBlock = getFuelBlock(starbase);
  if (!fuelBlock) return null;
  const rate = POS_FUEL_RATE[getTowerSize(starbase.typeName)];
  if (!rate) return null;
  return fuelBlock.quantity / rate;
};

// Format date
const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString();
};

// Fuel bar cell renderer — inline bar + days-remaining text
function FuelCellRenderer({ days, label }) {
  if (days === null || days === undefined) {
    return <span style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>—</span>;
  }
  const pct = Math.min(100, Math.max(0, (days / 30) * 100));
  const isDanger = days < 10;
  const isWarn = !isDanger && days < 14;
  const barClass = `fuel-bar${isDanger ? ' danger' : isWarn ? ' warn' : ''}`;
  const dayColor = isDanger ? 'var(--danger)' : isWarn ? 'var(--amber)' : 'var(--text-1)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'center', width: '100%', paddingRight: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div className={barClass} style={{ flex: 1, minWidth: 60 }}>
          <span className="fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="mono" style={{ fontSize: 11, color: dayColor, minWidth: 34, textAlign: 'right' }}>
          {Math.floor(days)}D
        </span>
      </div>
      {label && (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.08em' }}>
          {label}
        </span>
      )}
    </div>
  );
}

// State chip cell renderer
function StateCellRenderer({ state, reinforcedUntil }) {
  const isReinforced = state === 'reinforced';
  const isOffline = state === 'offline';
  const tone = isReinforced ? 'danger' : isOffline ? 'ghost' : 'ok';
  const dotTone = isReinforced ? 'danger' : '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center' }}>
      <Chip tone={tone}>
        <Dot tone={dotTone} />
        {(state || 'unknown').toUpperCase()}
      </Chip>
      {isReinforced && reinforcedUntil && (
        <span className="mono" style={{ fontSize: 10, color: 'var(--danger)', letterSpacing: '0.06em' }}>
          UNTIL {formatDate(reinforcedUntil)}
        </span>
      )}
    </div>
  );
}

function Structures() {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const [structures, setStructures] = useState([]);
  const [starbases, setStarbases] = useState([]);

  // Permission check — must be before any other hooks
  if (!user?.permissions?.canViewStructures) {
    return <NoAccess feature="Corporation Structures" requiredRole="Director, CEO, or Station Manager" />;
  }

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [fuelFilter, setFuelFilter] = useState('');

  // Fetch structures and starbases from MongoDB
  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [structuresRes, starbasesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/structures`),
        axios.get(`${API_BASE_URL}/structures/starbases`)
      ]);
      setStructures(structuresRes.data);
      setStarbases(starbasesRes.data);
    } catch (err) {
      console.error('Error fetching structures/starbases:', err);
      if (err.response?.data?.requiresReauth || err.response?.status === 401) {
        navigate('/?error=session_expired');
        return;
      }
      setError(err.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Sync structures from ESI
  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSyncMessage(null);
      const response = await axios.post(`${API_BASE_URL}/structures/sync`);
      const structureCount = response.data.structures?.count ?? 0;
      const starbaseCount = response.data.starbases?.count ?? 0;
      const warnings = response.data.warnings || [];
      setSyncMessage({
        type: warnings.length > 0 ? 'warning' : 'success',
        message: `Synced ${structureCount} structure(s) and ${starbaseCount} POS.${warnings.length ? ' ' + warnings.join(' ') : ''}`
      });
      await fetchAll();
    } catch (err) {
      console.error('Error syncing:', err);
      if (err.response?.data?.requiresReauth || err.response?.status === 401) {
        navigate('/?error=session_expired');
        return;
      }
      const errorMessage = err.response?.data?.error || 'Failed to sync';
      setError(errorMessage);
      setSyncMessage({ type: 'error', message: errorMessage });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Build unified row list
  const allItems = useMemo(() => {
    const structureItems = structures.map((s) => {
      const hours = s.fuelExpires ? (new Date(s.fuelExpires) - new Date()) / (1000 * 60 * 60) : Infinity;
      return { kind: 'structure', sortKey: hours, data: s };
    });
    const starbaseItems = starbases.map((s) => {
      let sortKey;
      if (s.state === 'reinforced') sortKey = -1;
      else if (s.state === 'offline') sortKey = -0.5;
      else {
        const hours = estimatePosHours(s);
        sortKey = hours === null ? Infinity : hours;
      }
      return { kind: 'starbase', sortKey, data: s };
    });
    return [...structureItems, ...starbaseItems].sort((a, b) => a.sortKey - b.sortKey);
  }, [structures, starbases]);

  // Client-side quick-filter on top of the unified list (ag-grid floating filters still work too)
  const filteredItems = useMemo(() => {
    return allItems.filter(({ kind, data }) => {
      // text search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const haystack = kind === 'structure'
          ? `${data.name || ''} ${data.systemName || ''}`.toLowerCase()
          : `${data.typeName || ''} ${data.systemName || ''} ${data.moonName || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      // type filter
      if (typeFilter) {
        if (typeFilter === 'pos' && kind !== 'starbase') return false;
        if (typeFilter === 'structure' && kind !== 'structure') return false;
      }
      // state filter
      if (stateFilter) {
        const state = (data.state || '').toLowerCase();
        if (stateFilter === 'reinforced' && state !== 'reinforced') return false;
        if (stateFilter === 'online' && state !== 'online') return false;
        if (stateFilter === 'offline' && state !== 'offline') return false;
      }
      // fuel tier filter
      if (fuelFilter) {
        let days = null;
        if (kind === 'structure') {
          const { days: d } = getStructureFuelStatus(data.fuelExpires);
          days = d;
        } else {
          const h = estimatePosHours(data);
          days = h === null ? null : h / 24;
        }
        if (fuelFilter === 'danger' && !(days !== null && days < 10)) return false;
        if (fuelFilter === 'warn' && !(days !== null && days >= 10 && days < 14)) return false;
        if (fuelFilter === 'ok' && !(days === null || days >= 14)) return false;
      }
      return true;
    });
  }, [allItems, searchQuery, typeFilter, stateFilter, fuelFilter]);

  // Telemetry counts from real data
  const telemetry = useMemo(() => {
    const total = allItems.length;
    const online = allItems.filter(({ data }) => (data.state || '').toLowerCase() === 'online').length;
    const reinforced = allItems.filter(({ data }) => (data.state || '').toLowerCase() === 'reinforced').length;
    let lowFuel = 0;
    allItems.forEach(({ kind, data }) => {
      if (kind === 'structure') {
        const { days } = getStructureFuelStatus(data.fuelExpires);
        if (days !== null && days < 14) lowFuel++;
      } else {
        const h = estimatePosHours(data);
        if (h !== null && h / 24 < 14) lowFuel++;
      }
    });
    // breakdown: count per kind
    const sCount = structures.length;
    const pCount = starbases.length;
    const parts = [];
    if (sCount) parts.push(`${sCount} UPWELL`);
    if (pCount) parts.push(`${pCount} POS`);
    return { total, online, reinforced, lowFuel, breakdown: parts.join(' · ') || '—' };
  }, [allItems, structures, starbases]);

  // Column definitions — HUD-skinned renderers
  const columnDefs = useMemo(() => [
    {
      headerName: '',
      width: 60,
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellStyle: { ...centeredCellStyle, justifyContent: 'center', padding: '4px 0' },
      cellRenderer: (params) => (
        <img
          src={getTypeIcon(params.data.data.typeId)}
          alt={params.data.data.typeName || ''}
          style={{ width: 36, height: 36, objectFit: 'contain' }}
        />
      )
    },
    {
      headerName: 'Kind',
      width: 110,
      valueGetter: (p) => p.data.kind === 'structure' ? 'Structure' : 'POS',
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <Chip tone={params.value === 'Structure' ? '' : 'amber'}>
          {params.value.toUpperCase()}
        </Chip>
      )
    },
    {
      headerName: 'Name / Location',
      flex: 2,
      minWidth: 200,
      valueGetter: (p) => p.data.kind === 'structure'
        ? (p.data.data.name || '')
        : (p.data.data.moonName || p.data.data.systemName || p.data.data.typeName || ''),
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <span className="cell-strong" style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.04em' }}>
          {params.value}
        </span>
      )
    },
    {
      headerName: 'System',
      width: 150,
      valueGetter: (p) => p.data.data.systemName || '',
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <span className="mono" style={{ fontSize: 11, color: 'var(--text-2)' }}>{params.value || '—'}</span>
      )
    },
    {
      headerName: 'Class',
      width: 210,
      valueGetter: (p) => p.data.data.typeName || '',
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <span className="mono" style={{ fontSize: 11, color: 'var(--text-2)', letterSpacing: '0.04em' }}>{params.value || '—'}</span>
      )
    },
    {
      headerName: 'State',
      width: 160,
      valueGetter: (p) => p.data.data.state || '',
      filter: 'agTextColumnFilter',
      cellStyle: { ...centeredCellStyle, padding: '6px 12px' },
      autoHeight: true,
      cellRenderer: (params) => (
        <StateCellRenderer
          state={params.data.data.state}
          reinforcedUntil={params.data.data.reinforcedUntil}
        />
      )
    },
    {
      headerName: 'Fuel',
      flex: 1,
      minWidth: 180,
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellStyle: { ...centeredCellStyle, padding: '6px 12px' },
      cellRenderer: (params) => {
        const { kind, data } = params.data;
        if (kind === 'structure') {
          const { days } = getStructureFuelStatus(data.fuelExpires);
          if (days === null) return <span style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>No data</span>;
          return <FuelCellRenderer days={days} label={data.fuelExpires ? `EXP ${formatDate(data.fuelExpires)}` : null} />;
        }
        // POS
        const fuelBlock = getFuelBlock(data);
        const h = estimatePosHours(data);
        const days = h === null ? null : h / 24;
        const qty = fuelBlock ? `${fuelBlock.quantity.toLocaleString()} BLK` : null;
        if (data.state === 'reinforced') {
          return <span className="mono" style={{ fontSize: 11, color: 'var(--danger)' }}>REINFORCED</span>;
        }
        if (data.state === 'offline') {
          return <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>OFFLINE</span>;
        }
        return <FuelCellRenderer days={days} label={qty} />;
      }
    },
    {
      headerName: 'Last Synced',
      width: 170,
      valueGetter: (p) => p.data.data.lastSyncedAt ? new Date(p.data.data.lastSyncedAt) : null,
      filter: 'agDateColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>
          {formatDate(params.data.data.lastSyncedAt)}
        </span>
      )
    }
  ], []);

  const total = allItems.length;
  const visible = filteredItems.length;

  return (
    <>
      <PageHead
        crumb="OPS // INFRASTRUCTURE"
        id={`${visible} OF ${total} VISIBLE`}
        title="Structures & Starbases"
        actions={
          <Btn
            icon={syncing ? <CircularProgress size={14} style={{ color: 'inherit' }} /> : I.Sync}
            tone="primary"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing…' : 'Sync from ESI'}
          </Btn>
        }
      />

      {/* Telemetry strip — tiles only shown when data exists */}
      <div className="telemetry-strip">
        <div className="tile">
          <div className="k">TOTAL</div>
          <div className="v">{total}<span className="unit">ASSETS</span></div>
          {telemetry.breakdown && <div className="delta mono">{telemetry.breakdown}</div>}
        </div>
        {telemetry.online > 0 && (
          <div className="tile ok">
            <div className="k">ONLINE</div>
            <div className="v">{telemetry.online}<span className="unit">ANCHORED</span></div>
          </div>
        )}
        {telemetry.lowFuel > 0 && (
          <div className="tile amber">
            <div className="k">LOW FUEL</div>
            <div className="v">{telemetry.lowFuel}<span className="unit">UNDER 14D</span></div>
            <div className="delta down mono" style={{ color: 'var(--amber)' }}>REFUEL REQUIRED</div>
          </div>
        )}
        {telemetry.reinforced > 0 && (
          <div className="tile magenta">
            <div className="k">REINFORCED</div>
            <div className="v">{telemetry.reinforced}<span className="unit">TIMERS LIVE</span></div>
          </div>
        )}
      </div>

      {/* Notifications */}
      {syncMessage && (
        <Alert severity={syncMessage.type} onClose={() => setSyncMessage(null)}>
          <AlertTitle>
            {syncMessage.type === 'success' && 'Sync Complete'}
            {syncMessage.type === 'warning' && 'Synced with Warnings'}
            {syncMessage.type === 'error' && 'Sync Failed'}
          </AlertTitle>
          {syncMessage.message}
        </Alert>
      )}
      {error && !syncMessage && (
        <Alert severity="error" onClose={() => setError(null)}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {/* Main table panel */}
      <Panel
        title="Asset Inventory"
        sub={`${visible} ROWS`}
        action={
          <Chip tone="ok">
            <Dot tone="pulse" />
            LIVE · ESI
          </Chip>
        }
      >
        {/* Filter bar */}
        <div className="filter-bar">
          <div className="hud-input search">
            {I.Search}
            <input
              type="text"
              placeholder="Search name, system, moon…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="hud-select"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
          >
            <option value="">All States</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="reinforced">Reinforced</option>
          </select>
          <select
            className="hud-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="structure">Upwell Structure</option>
            <option value="pos">POS / Starbase</option>
          </select>
          <select
            className="hud-select"
            value={fuelFilter}
            onChange={(e) => setFuelFilter(e.target.value)}
          >
            <option value="">All Fuel Tiers</option>
            <option value="danger">Critical (&lt;10D)</option>
            <option value="warn">Warning (10–14D)</option>
            <option value="ok">OK (14D+)</option>
          </select>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
            <CircularProgress size={48} style={{ color: 'var(--accent)' }} />
          </div>
        ) : total === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <p className="mono" style={{ color: 'var(--text-2)', marginBottom: 16 }}>
              No structures or POS found. Click &ldquo;Sync from ESI&rdquo; to fetch data.
            </p>
            <Btn icon={I.Sync} tone="primary" onClick={handleSync} disabled={syncing}>
              Sync Now
            </Btn>
          </div>
        ) : filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <p className="mono" style={{ color: 'var(--text-2)' }}>No matches — adjust filters above.</p>
          </div>
        ) : (
          <div className="hud-grid">
            <AgGridReact
              theme={gridTheme}
              rowData={filteredItems}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              domLayout="autoHeight"
              rowHeight={64}
              headerHeight={40}
              floatingFiltersHeight={36}
              pagination
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100]}
              getRowId={(params) =>
                params.data.kind === 'structure'
                  ? `structure-${params.data.data.structureId}`
                  : `starbase-${params.data.data.starbaseId}`
              }
              animateRows
            />
          </div>
        )}
      </Panel>
    </>
  );
}

export default Structures;
